import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { colors, starOrbs } from '@src/theme/sharedComponents'
import designConfig from '@design-config'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { IS_EMBEDDED } from '@src/constants/env'
import { getMapsUrl, getMapsEmbedUrl } from '@src/utils/maps'
import { formatPhone } from '@src/utils/phoneFormat'

const Section = styled.section<{ $stars?: boolean }>`
  background: ${colors.primaryDark};
  color: #fff;
  padding: 3rem 1.5rem 1.5rem;
  border-top: 3px solid ${colors.primary};
  position: relative;
  overflow: hidden;
  ${p => p.$stars && starOrbs}
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 2fr;
  gap: 2.5rem;

  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 576px) { grid-template-columns: 1fr; }
`

const Col = styled.div``

const ColTitle = styled.h5`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.1);
`

const CompanyDescription = styled.p`
  color: rgba(255,255,255,0.65);
  font-size: 0.88rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`

const SocialLinks = styled.div`
  display: flex;
  gap: 0.5rem;
`

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.6);
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;
  font-size: 0.95rem;

  &:hover { border-color: ${colors.accent}; color: ${colors.accent}; }
`

const FooterLink = styled.button`
  display: block;
  background: none;
  border: none;
  padding: 0 0 0.45rem;
  color: rgba(255,255,255,0.6);
  font-size: 0.88rem;
  text-align: left;
  cursor: pointer;
  transition: color 0.15s, padding-left 0.15s;

  &:hover { color: ${colors.accent}; padding-left: 6px; }
`

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  color: rgba(255,255,255,0.65);
  font-size: 0.88rem;

  i { color: ${colors.accent}; margin-right: 0.6rem; margin-top: 0.15rem; flex-shrink: 0; }
  a { color: inherit; text-decoration: none; transition: color 0.15s; &:hover { color: ${colors.accent}; } }
`

const MapWrapper = styled.div`
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1);

  iframe { display: block; width: 100%; height: 160px; border: 0; }
`

const Divider = styled.div`
  border-top: 1px solid rgba(255,255,255,0.08);
  margin: 2rem 0 1.25rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`

const BottomRow = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.35);
`

const SecurityBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;

  span { display: flex; align-items: center; gap: 0.35rem; }
  i { color: ${colors.accent}; }
`

const InfoSection: React.FC = () => {
  const navigate = useNavigate()
  const { data: s } = usePublicSettings()

  const mapsUrl = s ? getMapsUrl(s) : null
  const mapsEmbedUrl = s ? getMapsEmbedUrl(s) : null

  return (
    <Section $stars={designConfig.themeDecorations[s?.theme_id ?? ''] === 'stars'}>
      <Inner>
        <Col>
          <ColTitle>{s?.company_name || 'Company'}</ColTitle>
          {s?.company_tagline && <CompanyDescription>{s.company_tagline}</CompanyDescription>}
          <SocialLinks>
            {s?.social_facebook && <SocialLink href={s.social_facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="bi bi-facebook" /></SocialLink>}
            {s?.social_instagram && <SocialLink href={s.social_instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="bi bi-instagram" /></SocialLink>}
            {s?.social_linkedin && <SocialLink href={s.social_linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="bi bi-linkedin" /></SocialLink>}
          </SocialLinks>
        </Col>

        <Col>
          <ColTitle>Quick Links</ColTitle>
          {!IS_EMBEDDED && <FooterLink onClick={() => navigate('/')}>Home</FooterLink>}
          <FooterLink onClick={() => navigate('/products')}>Products</FooterLink>
          {!IS_EMBEDDED && <FooterLink onClick={() => navigate('/about')}>About Us</FooterLink>}
          {!IS_EMBEDDED && <FooterLink onClick={() => navigate('/contact')}>Contact Us</FooterLink>}
        </Col>

        <Col>
          <ColTitle>Contact</ColTitle>
          {s?.contact_phone && (
            <ContactItem><i className="bi bi-telephone-fill" /><a href={`tel:${s.contact_phone.replace(/\D/g, '')}`}>{formatPhone(s.contact_phone)}</a></ContactItem>
          )}
          {s?.contact_email && (
            <ContactItem><i className="bi bi-envelope-fill" /><a href={`mailto:${s.contact_email}`}>{s.contact_email}</a></ContactItem>
          )}
          {s?.contact_address_street && (
            <ContactItem>
              <i className="bi bi-geo-alt-fill" />
              <a href={mapsUrl || undefined} target="_blank" rel="noopener noreferrer">
                {s.contact_address_street}<br />
                {s.contact_address_city}, {s.contact_address_state} {s.contact_address_zip}
              </a>
            </ContactItem>
          )}
          {s?.contact_hours && <ContactItem><i className="bi bi-clock-fill" /><span>{s.contact_hours}</span></ContactItem>}
        </Col>

        {mapsEmbedUrl && (
          <Col>
            <ColTitle>Find Us</ColTitle>
            <MapWrapper>
              <iframe src={mapsEmbedUrl} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`${s?.company_name || 'Our'} Location`} />
            </MapWrapper>
          </Col>
        )}
      </Inner>

      <Divider />

      <BottomRow>
        <SecurityBadges>
          <span><i className="bi bi-shield-check" />OWASP Top 10</span>
          <span><i className="bi bi-layers" />5-Layer Security</span>
          <span><i className="bi bi-clipboard-check" />SOC 2 Ready</span>
        </SecurityBadges>
      </BottomRow>
    </Section>
  )
}

export default InfoSection
